/**
 * WS-HF — the TypeScript face of the desktop app's Higgsfield MCP client.
 *
 * Every function here is one `invoke("hf_mcp_*")` into the Rust module documented in
 * `apps/desktop/src-tauri/src/higgsfield_mcp/mod.rs`, plus the snake_case → camelCase mapping
 * the rest of the UI expects. **No token ever crosses this boundary**: the bearer lives in the
 * OS keychain and in Rust, and the widest thing a command returns is a credit balance.
 *
 * Availability is a CAPABILITY, exactly like `PlatformIO.downloadBytes` (WS-W): `hfMcpAvailable`
 * is true only inside a Tauri window. The web build therefore has no MCP surface at all — the
 * Settings section is not rendered, `generationSource` cannot be set to `higgsfield-mcp`, and
 * `runDeviceGeneration` is never reached. That is deliberate: a browser cannot hold the
 * operator's vendor credentials safely, and the cloud path is the web's only path.
 */

/** What `hfMcpStatus()` answers. `plan`/`credits` are absent when not connected. */
export interface HfMcpStatus {
  connected: boolean;
  plan?: string;
  credits?: number;
  account?: string;
  /** Why plan/credits are missing on a connected account (shown as a quiet sub-line). */
  error?: string;
}

/** What a started sign-in shows the operator. `userCode` is EMPTY for the
 *  authorization-code fallback, where the code rides in the URL instead. */
export interface HfMcpLoginStart {
  verificationUri: string;
  userCode: string;
  expiresIn: number;
  /** `"device_code"` or `"authorization_code"` — drives which sentence the dialog shows. */
  flow: string;
}

export interface HfMcpLoginPoll {
  status: "pending" | "connected";
  /** Seconds to wait before polling again (pending only). */
  afterSecs?: number;
}

export interface HfMcpBalance {
  credits: number;
  plan?: string;
}

export interface HfMcpTransaction {
  displayName: string;
  /** NEGATIVE for a spend — the sign is the information. */
  credits: number;
  action?: string;
  createdAt?: string;
}

export interface HfMcpTransactions {
  items: HfMcpTransaction[];
  nextCursor?: string;
}

export interface HfMcpCost {
  credits: number;
}

/** Terminal states, mirroring `JobOutcome` in Rust. Only `completed` carries URLs. */
export type HfMcpJobState =
  | "queued"
  | "in_progress"
  | "completed"
  | "failed"
  | "nsfw"
  | "canceled";

export interface HfMcpJobStatus {
  jobId: string;
  status: HfMcpJobState;
  /** Result image URLs. EMPTY unless `status === "completed"` — a refused job never
   *  exposes one, which is what keeps "moderated" from reading as "done". */
  urls: string[];
  error?: string;
}

export interface HfMcpWaitResult {
  jobs: HfMcpJobStatus[];
  allTerminal: boolean;
  pollAfterSeconds?: number;
}

export interface HfMcpGenerateInput {
  model: string;
  prompt: string;
  /** Model-specific params (`resolution`, `quality`, `aspect_ratio`, `count`, …). */
  params?: Record<string, unknown>;
  /**
   * Pictures to import and attach, IN ORDER: the design first, then the template's case
   * photo. The order mirrors the cloud relay (products first, then references) and is
   * preserved all the way into `medias[]`.
   */
  inputUrls?: string[];
}

/** The Tauri `invoke`, narrowed to what this module needs. */
export type HfMcpInvoke = <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>;

/**
 * The Tauri v2 window's own `invoke`, or `undefined` outside a Tauri window. Mirrors
 * `desktop.ts`'s `windowTauriInvoke` — duplicated rather than exported from there so this
 * module stays independent of the PlatformIO implementation.
 */
function windowInvoke(): HfMcpInvoke | undefined {
  const g = globalThis as unknown as {
    __TAURI_INTERNALS__?: { invoke?: HfMcpInvoke };
  };
  const inv = g.__TAURI_INTERNALS__?.invoke;
  return typeof inv === "function" ? (inv.bind(g.__TAURI_INTERNALS__) as HfMcpInvoke) : undefined;
}

/** Wire shapes, as `serde` serialises them (snake_case, except `LoginPoll`). */
interface WireStatus {
  connected: boolean;
  plan?: string | null;
  credits?: number | null;
  account?: string | null;
  error?: string | null;
}
interface WireStart {
  verification_uri: string;
  user_code: string;
  expires_in: number;
  flow: string;
}
interface WireBalance {
  credits: number;
  subscription_plan_type?: string | null;
}
interface WireTransactions {
  items: {
    display_name: string;
    credits: number;
    action?: string | null;
    created_at?: string | null;
  }[];
  next_cursor?: string | null;
}
interface WireWait {
  jobs: { job_id: string; status: { kind: HfMcpJobState }; urls: string[]; error?: string | null }[];
  all_terminal: boolean;
  poll_after_seconds?: number | null;
}

/** `null` and `undefined` are the same absence to the UI; collapse them once, here. */
function opt<T>(v: T | null | undefined): T | undefined {
  return v ?? undefined;
}

/**
 * The client. Constructed with an `invoke` so the tests drive it with a mock bridge and never
 * need a Tauri window — the same seam `DesktopPlatformIO` uses for the secret store.
 */
export class HiggsfieldMcpClient {
  readonly hfMcpAvailable: boolean;
  private readonly invoke: HfMcpInvoke | undefined;

  constructor(invoke?: HfMcpInvoke) {
    this.invoke = invoke ?? windowInvoke();
    this.hfMcpAvailable = this.invoke !== undefined;
  }

  private call<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
    if (!this.invoke) {
      // The web build must never reach here — `hfMcpAvailable` is the guard, and this throw
      // is what makes a missing guard a loud failure rather than a silent no-op.
      return Promise.reject(new Error("Higgsfield generation requires the desktop app."));
    }
    return this.invoke<T>(cmd, args);
  }

  async hfMcpStatus(): Promise<HfMcpStatus> {
    if (!this.hfMcpAvailable) return { connected: false };
    const r = await this.call<WireStatus>("hf_mcp_status");
    return {
      connected: !!r.connected,
      plan: opt(r.plan),
      credits: opt(r.credits),
      account: opt(r.account),
      error: opt(r.error),
    };
  }

  async hfMcpLoginStart(): Promise<HfMcpLoginStart> {
    const r = await this.call<WireStart>("hf_mcp_login_start");
    return {
      verificationUri: r.verification_uri,
      userCode: r.user_code ?? "",
      expiresIn: r.expires_in ?? 0,
      flow: r.flow ?? "device_code",
    };
  }

  hfMcpLoginPoll(): Promise<HfMcpLoginPoll> {
    return this.call<HfMcpLoginPoll>("hf_mcp_login_poll");
  }

  /** Open the sign-in page in the SYSTEM browser. Rust refuses any URL that is not the one
   *  the current sign-in produced, so this cannot be pointed elsewhere. */
  hfMcpOpenLogin(url: string): Promise<void> {
    return this.call<void>("hf_mcp_open_login", { url });
  }

  /** Abandon an in-flight sign-in. Rust drops the login handle, which CLOSES the loopback
   *  listener — without this the thread keeps a registered port the next attempt needs. */
  hfMcpLoginCancel(): Promise<void> {
    return this.call<void>("hf_mcp_login_cancel");
  }

  hfMcpLogout(): Promise<void> {
    return this.call<void>("hf_mcp_logout");
  }

  async hfMcpBalance(): Promise<HfMcpBalance> {
    const r = await this.call<WireBalance>("hf_mcp_balance");
    return { credits: r.credits, plan: opt(r.subscription_plan_type) };
  }

  async hfMcpTransactions(size: number, cursor?: string): Promise<HfMcpTransactions> {
    const r = await this.call<WireTransactions>("hf_mcp_transactions", { size, cursor });
    return {
      items: (r.items ?? []).map((row) => ({
        displayName: row.display_name,
        credits: row.credits,
        action: opt(row.action),
        createdAt: opt(row.created_at),
      })),
      nextCursor: opt(r.next_cursor),
    };
  }

  hfMcpEstimate(model: string, params: Record<string, unknown> = {}): Promise<HfMcpCost> {
    return this.call<HfMcpCost>("hf_mcp_estimate", { model, params });
  }

  /** Submit one generation. Resolves the job ids — generation itself is asynchronous. */
  async hfMcpGenerate(input: HfMcpGenerateInput): Promise<{ jobId: string; jobIds: string[] }> {
    const ids = await this.call<string[]>("hf_mcp_generate", {
      model: input.model,
      prompt: input.prompt,
      params: input.params ?? {},
      inputUrls: input.inputUrls ?? [],
    });
    if (!ids?.length) throw new Error("Higgsfield did not start a generation job.");
    return { jobId: ids[0], jobIds: ids };
  }

  async hfMcpWait(jobIds: string[], timeoutSeconds?: number): Promise<HfMcpWaitResult> {
    const r = await this.call<WireWait>("hf_mcp_wait", { jobIds, timeoutSeconds });
    return {
      jobs: (r.jobs ?? []).map((j) => ({
        jobId: j.job_id,
        // `JobOutcome` is an internally-tagged enum in Rust (`{"kind":"nsfw"}`); a future
        // plain-string encoding is read too so this layer is not the thing that breaks.
        status: (typeof j.status === "string" ? j.status : j.status?.kind) as HfMcpJobState,
        urls: j.urls ?? [],
        error: opt(j.error),
      })),
      allTerminal: !!r.all_terminal,
      pollAfterSeconds: opt(r.poll_after_seconds),
    };
  }
}

/** The process-wide client, bound to this window's Tauri bridge (or to nothing, on web). */
let singleton: HiggsfieldMcpClient | undefined;

export function higgsfieldMcp(): HiggsfieldMcpClient {
  if (!singleton) singleton = new HiggsfieldMcpClient();
  return singleton;
}

/** Tests and the desktop bootstrap replace the client. Passing `undefined` restores the
 *  window-bound default on the next call. */
export function setHiggsfieldMcp(client: HiggsfieldMcpClient | undefined): void {
  singleton = client;
}

/** Is there an MCP surface at all? False on web, false in a plain browser `vite dev`. */
export function hfMcpAvailable(): boolean {
  return higgsfieldMcp().hfMcpAvailable;
}
